import { t } from './i18n';
import { store, WeatherType, LanguageType } from '../app/store';
import { QualityPreset } from '../config/quality';
import { openAboutModal } from './aboutModal';
import { AudioPlayer } from '../audio/player';
import { SCENE_DEFINITIONS } from '../config/scenes';

export function openSettingsModal(audioPlayer?: AudioPlayer): void {
  const container = document.getElementById('modal-container');
  const card = document.getElementById('modal-card');
  if (!container || !card) return;

  const state = store.getState();
  const lang = state.language;
  const currentSceneDef = SCENE_DEFINITIONS.find(s => s.id === state.sceneId) || SCENE_DEFINITIONS[0];
  const supportsSnow = currentSceneDef.supportedWeather.includes('snow');

  card.innerHTML = `
    <div class="modal-header">
      <h3 class="modal-title">${t('settings_title', lang)}</h3>
      <button class="modal-close-btn" id="modal-close-btn" aria-label="Close">&times;</button>
    </div>

    <!-- Atmosphere -->
    <div class="setting-group">
      <div class="setting-title">${t('settings_atmosphere', lang)}</div>

      <div class="setting-row">
        <span>${t('settings_time', lang)}</span>
        <div class="setting-control" style="flex-wrap: wrap;">
          <button class="segmented-btn ${state.timeOfDay === 0 ? 'active' : ''}" data-time="0">0: Dawn</button>
          <button class="segmented-btn ${state.timeOfDay === 1 ? 'active' : ''}" data-time="1">1: Morning</button>
          <button class="segmented-btn ${state.timeOfDay === 2 ? 'active' : ''}" data-time="2">2: Noon</button>
          <button class="segmented-btn ${state.timeOfDay === 3 ? 'active' : ''}" data-time="3">3: Golden</button>
          <button class="segmented-btn ${state.timeOfDay === 4 ? 'active' : ''}" data-time="4">4: Dusk</button>
          <button class="segmented-btn ${state.timeOfDay === 5 ? 'active' : ''}" data-time="5">5: Night</button>
        </div>
      </div>

      <div class="setting-row">
        <span>${t('settings_weather', lang)}</span>
        <div class="setting-control">
          <button class="segmented-btn ${state.weather === 'sunny' ? 'active' : ''}" data-weather="sunny">${t('weather_sunny', lang)}</button>
          <button class="segmented-btn ${state.weather === 'rain' ? 'active' : ''}" data-weather="rain">${t('weather_rain', lang)}</button>
          <button class="segmented-btn ${state.weather === 'snow' ? 'active' : ''} ${!supportsSnow ? 'disabled' : ''}" data-weather="snow" ${!supportsSnow ? 'disabled title="' + t('weather_restricted', lang) + '"' : ''}>${t('weather_snow', lang)}</button>
        </div>
      </div>
    </div>

    <!-- Music & Audio -->
    <div class="setting-group">
      <div class="setting-title">Âm nhạc & Thư giãn</div>

      <div class="setting-row">
        <span>Âm lượng (${state.audioVolume}%)</span>
        <input type="range" id="input-volume" min="0" max="100" value="${state.audioVolume}" style="width: 140px; accent-color: var(--accent-blue);" />
      </div>

      <div class="setting-row">
        <span>${t('audio_sleep_timer', lang)}</span>
        <div class="setting-control">
          <button class="segmented-btn ${state.sleepTimerMinutes === null ? 'active' : ''}" data-sleep="0">${t('audio_sleep_off', lang)}</button>
          <button class="segmented-btn ${state.sleepTimerMinutes === 15 ? 'active' : ''}" data-sleep="15">15m</button>
          <button class="segmented-btn ${state.sleepTimerMinutes === 30 ? 'active' : ''}" data-sleep="30">30m</button>
          <button class="segmented-btn ${state.sleepTimerMinutes === 60 ? 'active' : ''}" data-sleep="60">60m</button>
        </div>
      </div>

      <div class="setting-row" style="margin-top: 0.5rem;">
        <div>
          <button id="btn-add-songs" class="pill-btn" style="font-size: 0.8rem; padding: 0.35rem 0.8rem;">
            📁 ${t('audio_add_songs', lang)}
          </button>
          <input type="file" id="input-local-files" multiple accept="audio/*" style="display: none;" />
        </div>
        <span style="font-size: 0.725rem; color: #64748b; max-width: 220px; text-align: right;">${t('audio_add_songs_hint', lang)}</span>
      </div>
    </div>

    <!-- Graphics & Quality -->
    <div class="setting-group">
      <div class="setting-title">${t('settings_visuals', lang)}</div>

      <div class="setting-row">
        <span>${t('settings_quality', lang)}</span>
        <div class="setting-control">
          <button class="segmented-btn ${state.quality === 'auto' ? 'active' : ''}" data-quality="auto">${t('quality_auto', lang)}</button>
          <button class="segmented-btn ${state.quality === 'low' ? 'active' : ''}" data-quality="low">${t('quality_low', lang)}</button>
          <button class="segmented-btn ${state.quality === 'medium' ? 'active' : ''}" data-quality="medium">${t('quality_medium', lang)}</button>
          <button class="segmented-btn ${state.quality === 'high' ? 'active' : ''}" data-quality="high">${t('quality_high', lang)}</button>
        </div>
      </div>

      <div class="setting-row">
        <span>${t('settings_optimize', lang)}</span>
        <input type="checkbox" id="check-optimize" ${state.optimizePerformance ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>

      <div class="setting-row">
        <span>${t('settings_tiltshift', lang)}</span>
        <input type="checkbox" id="check-tiltshift" ${state.tiltShift ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>

      <div class="setting-row">
        <span>${t('settings_pixelart', lang)}</span>
        <input type="checkbox" id="check-pixelart" ${state.pixelArt ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>

      <div class="setting-row">
        <span>${t('settings_inklines', lang)}</span>
        <input type="checkbox" id="check-inklines" ${state.inkLines ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>

      <div class="setting-row">
        <span>${t('settings_bloom', lang)}</span>
        <input type="checkbox" id="check-bloom" ${state.bloom ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>

      <div class="setting-row">
        <span>${t('settings_reduce_motion', lang)}</span>
        <input type="checkbox" id="check-reduce-motion" ${state.reduceMotion ? 'checked' : ''} style="width: 20px; height: 20px;" />
      </div>
    </div>

    <!-- Language -->
    <div class="setting-group">
      <div class="setting-title">${t('settings_language', lang)}</div>
      <div class="setting-control" style="width: 100%;">
        <button class="segmented-btn ${lang === 'vi' ? 'active' : ''}" data-lang="vi" style="flex: 1;">Tiếng Việt</button>
        <button class="segmented-btn ${lang === 'en' ? 'active' : ''}" data-lang="en" style="flex: 1;">English</button>
        <button class="segmented-btn ${lang === 'zh-CN' ? 'active' : ''}" data-lang="zh-CN" style="flex: 1;">简体中文</button>
      </div>
    </div>

    <!-- Actions -->
    <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255,255,255,0.1); padding-top: 1rem; margin-top: 1.5rem;">
      <button id="btn-reset-settings" class="pill-btn" style="color: #f87171; border-color: rgba(248,113,113,0.3); font-size: 0.8rem;">
        ${t('settings_reset', lang)}
      </button>
      <button id="btn-open-about" class="pill-btn" style="font-size: 0.8rem;">
        ${t('settings_about_btn', lang)}
      </button>
    </div>
  `;

  container.classList.remove('hidden');

  // Close handlers
  const closeBtn = document.getElementById('modal-close-btn');
  const backdrop = document.getElementById('modal-backdrop');
  const closeModal = () => container.classList.add('hidden');
  closeBtn?.addEventListener('click', closeModal, { once: true });
  backdrop?.addEventListener('click', closeModal, { once: true });

  // Time buttons
  card.querySelectorAll<HTMLButtonElement>('[data-time]').forEach(btn => {
    btn.addEventListener('click', () => {
      const timeVal = parseInt(btn.getAttribute('data-time') || '0', 10);
      store.setState({ timeOfDay: timeVal });
      openSettingsModal(audioPlayer); // re-render state
    });
  });

  // Weather buttons
  card.querySelectorAll<HTMLButtonElement>('[data-weather]').forEach(btn => {
    btn.addEventListener('click', () => {
      const weatherVal = btn.getAttribute('data-weather') as WeatherType;
      store.setState({ weather: weatherVal });
      openSettingsModal(audioPlayer);
    });
  });

  // Quality buttons
  card.querySelectorAll<HTMLButtonElement>('[data-quality]').forEach(btn => {
    btn.addEventListener('click', () => {
      const qVal = btn.getAttribute('data-quality') as QualityPreset;
      store.setState({ quality: qVal });
      openSettingsModal(audioPlayer);
    });
  });

  // Language buttons
  card.querySelectorAll<HTMLButtonElement>('[data-lang]').forEach(btn => {
    btn.addEventListener('click', () => {
      const langVal = btn.getAttribute('data-lang') as LanguageType;
      store.setState({ language: langVal });
      openSettingsModal(audioPlayer);
    });
  });

  // Sleep timer buttons
  card.querySelectorAll<HTMLButtonElement>('[data-sleep]').forEach(btn => {
    btn.addEventListener('click', () => {
      const sleepMin = parseInt(btn.getAttribute('data-sleep') || '0', 10);
      audioPlayer?.setSleepTimer(sleepMin === 0 ? null : sleepMin);
      openSettingsModal(audioPlayer);
    });
  });

  // Volume slider
  const volInput = document.getElementById('input-volume') as HTMLInputElement;
  volInput?.addEventListener('input', () => {
    const vol = parseInt(volInput.value, 10);
    audioPlayer?.setVolume(vol);
  });

  // Local song upload
  const btnAddSongs = document.getElementById('btn-add-songs');
  const inputLocalFiles = document.getElementById('input-local-files') as HTMLInputElement;
  btnAddSongs?.addEventListener('click', () => {
    inputLocalFiles?.click();
  });
  inputLocalFiles?.addEventListener('change', () => {
    if (inputLocalFiles.files && inputLocalFiles.files.length > 0) {
      audioPlayer?.addLocalSongs(inputLocalFiles.files);
      closeModal();
    }
  });

  // Toggles
  const checkOptimize = document.getElementById('check-optimize') as HTMLInputElement;
  checkOptimize?.addEventListener('change', () => {
    store.setState({ optimizePerformance: checkOptimize.checked });
  });

  const checkTiltshift = document.getElementById('check-tiltshift') as HTMLInputElement;
  checkTiltshift?.addEventListener('change', () => {
    store.setState({ tiltShift: checkTiltshift.checked });
  });

  const checkPixelart = document.getElementById('check-pixelart') as HTMLInputElement;
  checkPixelart?.addEventListener('change', () => {
    store.setState({ pixelArt: checkPixelart.checked });
  });

  const checkInklines = document.getElementById('check-inklines') as HTMLInputElement;
  checkInklines?.addEventListener('change', () => {
    store.setState({ inkLines: checkInklines.checked });
  });

  const checkBloom = document.getElementById('check-bloom') as HTMLInputElement;
  checkBloom?.addEventListener('change', () => {
    store.setState({ bloom: checkBloom.checked });
  });

  const checkReduceMotion = document.getElementById('check-reduce-motion') as HTMLInputElement;
  checkReduceMotion?.addEventListener('change', () => {
    store.setState({ reduceMotion: checkReduceMotion.checked });
  });

  // Reset settings
  document.getElementById('btn-reset-settings')?.addEventListener('click', () => {
    store.resetSettings();
    openSettingsModal(audioPlayer);
  });

  // About modal link
  document.getElementById('btn-open-about')?.addEventListener('click', () => {
    closeModal();
    openAboutModal();
  });
}
